import colors from '@mathflat/common/style/colors'
import clsx from 'clsx'
import { nanoid } from 'nanoid'
import type { ComponentProps, CSSProperties } from 'react'
import { useContext, useMemo } from 'react'

import { Icon } from '@/component/atoms/Icon'
import type { DefaultProps } from '@/types'

import { RadioGroupContext } from '../Radio/context'
import S from './ChoiceChip.style'

type Props = {
  value?: string
  iconName?: ComponentProps<typeof Icon>['name']
  disabled?: boolean
  rounded?: boolean
  newTheme?: 'sm' | 'md' | 'kmm' | 'weizmann'
  width?: CSSProperties['width']
  onChange?: (e: React.ChangeEvent, next: () => void) => void
} & DefaultProps

const ChoiceChip = ({
  value,
  iconName,
  disabled = false,
  rounded = false,
  newTheme,
  width,
  children,
  className,
  style,
  onChange,
}: Props) => {
  const context = useContext(RadioGroupContext)!
  const id = useMemo(() => nanoid(), [])
  const controlledParams = (context.value && { checked: context.value === value }) || undefined

  let inputCss = S.input
  let labelCss = S.label

  if (newTheme === 'kmm') {
    inputCss = S.kmmInput
    labelCss = S.kmmLabel
  } else if (newTheme === 'weizmann') {
    inputCss = S.weizmannInput
    labelCss = S.weizmannLabel
  } else if (newTheme) {
    inputCss = S.newInput
    labelCss = S.newLabel
  }
  return (
    <>
      <input
        id={id}
        css={inputCss}
        name={context.name}
        type="radio"
        value={value}
        ref={context.ref}
        onChange={(e) => {
          if (onChange) {
            onChange?.(e, () => context.onChange(e))
            return
          }
          context.onChange(e)
        }}
        disabled={disabled}
        {...controlledParams}
      />
      <label
        className={clsx(
          'choice-chip-label',
          newTheme && `new-theme-${newTheme}`,
          rounded && 'rounded',
          className,
        )}
        css={[labelCss, width && { width }, iconName && { padding: '16px 14px' }]}
        style={style}
        htmlFor={id}
      >
        {iconName && (
          <Icon name={iconName} className="choice-chip-icon" size={20} color={colors.gray.$500} />
        )}
        {children}
      </label>
    </>
  )
}

export default ChoiceChip
